import type { ResumeFormat, ResumeRole } from "@/lib/api/resumeService";
import { isDayOnly } from "@/lib/format";

/** Dates as the posting's page and the resume set them. */

/** "6 Oct, 09:12", or "10 Oct 2026, 09:00" with the year. */
export function stamp(value: string, withYear = false, timeZone?: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const day = new Intl.DateTimeFormat("en-GB", {
        day: "numeric",
        month: "short",
        year: withYear ? "numeric" : undefined,
        timeZone,
    }).format(date);
    const time = new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
        timeZone,
    }).format(date);
    return `${monthsAsDesigned(day)}, ${time}`;
}

/** Some engines spell September "Sept" in British English; the design says "Sep". */
function monthsAsDesigned(text: string): string {
    return text.replace("Sept", "Sep");
}

/** "10 Oct". */
export function dayOf(value: string, timeZone?: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return monthsAsDesigned(
        new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone }).format(date),
    );
}

/** The day a posting went up: "10 Oct". One that states only a day keeps it in every zone. */
export function postedDay(value: string, timeZone?: string): string {
    return dayOf(value, isDayOnly(value) ? "UTC" : timeZone);
}

/** When a posting went up: "10 Oct 2026, 09:00", or "10 Oct 2026" where it states no time. */
export function postedStamp(value: string, timeZone?: string): string {
    if (!isDayOnly(value)) return stamp(value, true, timeZone);
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return monthsAsDesigned(
        new Intl.DateTimeFormat("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
            timeZone: "UTC",
        }).format(date),
    );
}

/** When a posting went up, in words: "10 October at 09:00", or "10 October". */
export function postedInFull(value: string, timeZone?: string): string {
    if (isDayOnly(value)) return dayInFull(value, "UTC");
    return `${dayInFull(value, timeZone)} at ${stamp(value, false, timeZone).split(", ")[1]}`;
}

/** "10 October". */
export function dayInFull(value: string, timeZone?: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", timeZone }).format(
        date,
    );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * A resume's date as its format sets it: `2021-03`, or `Mar 2021`. `present`, in any case, as
 * `Present`. Anything else as written.
 */
export function resumeDate(value: string, style: ResumeFormat["dates"]): string {
    if (value.trim().toLowerCase() === "present") return "Present";
    const match = /^(\d{4})-(\d{2})$/.exec(value.trim());
    if (style !== "MONTH_YEAR" || !match) return value;
    const month = MONTHS[Number(match[2]) - 1];
    return month ? `${month} ${match[1]}` : value;
}

/** "Mar 2021 to Present". */
export function roleSpan(role: ResumeRole, style: ResumeFormat["dates"]): string {
    const start = role.start ? resumeDate(role.start, style) : null;
    const end = role.end ? resumeDate(role.end, style) : null;
    if (start && end) return `${start} to ${end}`;
    return start ?? end ?? "";
}
